import { defineStore } from "pinia";
import { ref } from "vue";

import type { WelcomeDeckId } from "@/features/WelcomeFlow/decks/WelcomeDeck";
import type { WelcomeVariant } from "@/features/WelcomeFlow/types/WelcomeFlowTypes";

export type ActiveWelcomeTour = {
  deck: WelcomeDeckId;
  variant: WelcomeVariant;
  // Whether this run opened right after the start dialog created a project.
  afterCreation: boolean;
};

// Which deck is currently mounted as an anchored tour (App.vue renders <WelcomeTour> off this). A
// store rather than a module-level ref: two unrelated parts of the app read/write it — App.vue's
// auth watcher and the nav ⓘ replay path — and a plain module-level ref is fragile under Vite
// HMR, where the two importers can end up resolving different module instances of the same file.
export const useWelcomeFlowStore = defineStore("welcomeFlow", () => {
  // state
  const activeTour = ref<ActiveWelcomeTour | null>(null);
  // A tour opened on demand (e.g. the viewer guide, from the sidebar's info button) rather than
  // through the once-per-user check — Skip/Escape always available, and (being for a deck with no
  // flowKey) never written or read as seen.
  const isOnDemand = ref(false);
  // Set by StartProjectModal before redirecting; consumed once by the project tour's trigger.
  const nextProjectTourAfterCreation = ref(false);
  // Which menu key (if any) the current guide step is teaching by holding it open — see Popover.vue's
  // `heldOpen` prop. Null whenever no step asks for one, and always cleared with the tour itself.
  const heldOpenMenu = ref<string | null>(null);

  // actions
  const openTour = (deck: WelcomeDeckId, variant: WelcomeVariant, options?: { afterCreation?: boolean; onDemand?: boolean }) => {
    activeTour.value = { deck, variant, afterCreation: options?.afterCreation ?? false };
    isOnDemand.value = options?.onDemand ?? false;
  };

  const closeTour = () => {
    activeTour.value = null;
    isOnDemand.value = false;
    heldOpenMenu.value = null;
  };

  const holdMenuOpen = (key: string | null) => {
    heldOpenMenu.value = key;
  };

  const markNextProjectTourAfterCreation = () => {
    nextProjectTourAfterCreation.value = true;
  };

  // Read once, then reset, so a later visit doesn't also claim "right after creation".
  const consumeNextProjectTourAfterCreation = (): boolean => {
    const value = nextProjectTourAfterCreation.value;
    nextProjectTourAfterCreation.value = false;
    return value;
  };

  return {
    // state
    activeTour,
    isOnDemand,
    nextProjectTourAfterCreation,
    heldOpenMenu,

    // actions
    openTour,
    closeTour,
    markNextProjectTourAfterCreation,
    consumeNextProjectTourAfterCreation,
    holdMenuOpen,
  };
});
